import * as React from 'react';
import axios from 'axios';
import LogPlusAnyResourcesAutocomplete from './LogPlusAnyResourcesAutocomplete';
import { AutocompleteMode, ILicenseListItem, IRelatedLicenseListItem, parseToLicense, parseFromLicense } from './IModels';
import { Typography } from '@material-ui/core';

interface GetLicensesAutocompleteResult {
	success: boolean,
	status?: "LogDisabled",
	items?: ILicenseListItem[]
}

export interface IAutocompleteParams {
	maxRows: number,
	filterId?: string,
}

interface IProps<T> {
	value: T[],
	onSelect: (value: T[]) => void,
	multiple?: true | false,
	placeholderText?: string,
	error: boolean;
	helperText: string,
	searchIn: string,
	mode: AutocompleteMode,
	notifier: { value: string, label: string },
	filterId: string,
	relatedFilterId: string,
}

export default function LogPlusLicensesAutocomplete(props: IProps<ILicenseListItem>) {

	function parseRelated() {
		return props.value.map(parseFromLicense);
	}

	function parseFromRelated(r: IRelatedLicenseListItem[]) {
		return r.map(parseToLicense);
	}

	switch (props.mode) {
		case 'relatedUser':
		case 'relatedNotifier':
			return (<RelatedLicensesAutocomplete
				searchIn={props.searchIn?.split(',') as (keyof IRelatedLicenseListItem)[]}
				notifierId={props.notifier?.value}
				mode={props.mode}
				error={props.error}
				helperText={props.helperText}
				multiple={props.multiple}
				value={parseRelated()}
				onSelect={(r) => {
					let data = parseFromRelated(r);
					props.onSelect(data);
				}}
				filterId={props.relatedFilterId}
			/>);
		default:
			return (<LicenseAutocomplete
				value={props.value}
				onSelect={props.onSelect}

				searchIn={props.searchIn?.split(',') as (keyof ILicenseListItem)[]}
				error={props.error}
				helperText={props.helperText}
				multiple={props.multiple}
				filterId={props.filterId}
			/>);
	}
}

interface ILicenseAutocompleteProps {
	value: ILicenseListItem[],
	onSelect: (value: ILicenseListItem[]) => void,
	multiple?: true | false,
	placeholderText?: string,
	error?: boolean;
	helperText?: string,

	searchIn: (keyof ILicenseListItem)[],
	filterId: string,
}

function LicenseAutocomplete(props: ILicenseAutocompleteProps) {
	const getOptionSelected = (option: ILicenseListItem, value: ILicenseListItem) => {
		return option.Id === value.Id;
	}

	return (<LogPlusAnyResourcesAutocomplete
		{...props}
		getOptionLabel={(option: ILicenseListItem) => option.Name}
		getOptionSelected={getOptionSelected}
		createRequest={(text: string) => {
			return {
				text: text,
			}
		}}
		loadOptions={(request, callback) => {

			axios.get<GetLicensesAutocompleteResult>('/LogPlusResources/GetLicensesAutocomplete/', {
				params: {
					search: request.text,
					searchIn: props.searchIn?.join(','),
					max: 20,
					filterId: props.filterId,
				}
			})
				.then(r => {
					callback(r.data.items);
				});
		}}
		renderOption={(option) => {
			return (<div>
				{option.Name}
				<Typography variant="body2" color="textSecondary">
					{option.Description}
				</Typography>
			</div>);
		}}
	/>)
}

export interface GetRelatedLicensesAutocompleteResourcesResult {
	success: boolean,
	status?: "LogDisabled",
	items?: IRelatedLicenseListItem[]
}

interface IRelatedLicensesAutocompleteProps {
	value: IRelatedLicenseListItem[],
	onSelect: (value: IRelatedLicenseListItem[]) => void,
	multiple?: true | false,
	placeholderText?: string,
	error?: boolean;
	helperText?: string,

	notifierId: string | null,
	searchIn: (keyof IRelatedLicenseListItem)[],
	mode: AutocompleteMode,
	filterId: string,
}

function RelatedLicensesAutocomplete(props: IRelatedLicensesAutocompleteProps) {

	let notifierId: string = null
	if (props.mode === 'relatedNotifier') {
		notifierId = props.notifierId;
	}

	return (<LogPlusAnyResourcesAutocomplete
		{...props}
		getOptionLabel={(option: IRelatedLicenseListItem) => option.Product}
		getOptionSelected={(o, v) => o.Id === v.Id}
		cachePrefix={notifierId}
		createRequest={(text: string) => {
			return {
				text: text,
				notifierId: notifierId,
			}
		}}
		loadOptions={(request, callback) => {

			if (!request.notifierId && props.mode === 'relatedNotifier') {
				callback([]);
			}

			axios.get<GetRelatedLicensesAutocompleteResourcesResult>('/LogPlusResources/GetRelatedLicensesAutocomplete/', {
				params: {
					notifierId: request.notifierId,
					search: request.text,
					searchIn: props.searchIn?.join(','),
					max: 20,
					filterId: props.filterId,
				}
			})
				.then(r => {
					callback(r.data.items);
				});
		}}
		renderOption={(option) => {
			return (<div>
				{option.Product}
				<Typography variant="body2" color="textSecondary">
					{option.Description}
				</Typography>
			</div>);
		}}
	/>);
}